import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Divider,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Slider,
  Tooltip,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  QrCode as QrCodeIcon,
  Delete as DeleteIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import { v4 as uuidv4 } from 'uuid';
import api from '../config/api';
import { toast } from 'react-toastify';
import { useApiError } from '../hooks/useApiError';

function Instancias() {
  const [instancias, setInstancias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [qrCodeLoading, setQrCodeLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [openQrDialog, setOpenQrDialog] = useState(false);
  const [openConfigDialog, setOpenConfigDialog] = useState(false);
  const [selectedInstancia, setSelectedInstancia] = useState(null);
  const [qrCodeData, setQrCodeData] = useState(null);
  const [formData, setFormData] = useState({
    nome: '',
    numero_whatsapp: '',
    token: uuidv4()
  });
  const [configData, setConfigData] = useState({
    rejeitar_ligacao: false,
    mensagem_ligacao_rejeitada: '',
    marcar_mensagem_como_lida: false,
    mostrar_sempre_online: false
  });
  const [rateLimitData, setRateLimitData] = useState({
    mensagens_por_minuto: 15,
    idade_conta_meses: ''
  });
  const [sugestoesRate, setSugestoesRate] = useState([]);

  const { handleError } = useApiError();

  useEffect(() => {
    fetchInstancias();
    // Carrega sugestões de rate-limit (endpoint público)
    api.get('/instancias/sugestoes-rate-limit')
      .then(r => setSugestoesRate(r.data.sugestoes || []))
      .catch(() => { /* silencioso: UI mostra inputs livres mesmo sem sugestões */ });
  }, []);

  // Polling para verificar o status da conexão quando o QR Code está sendo exibido
  useEffect(() => {
    let intervalId;
    
    if (openQrDialog && selectedInstancia) {
      intervalId = setInterval(async () => {
        try {
          const response = await api.get(`/instancias/${selectedInstancia.idInstancia}`);
          
          if (response.data.status_conexao === 'conectado') {
            clearInterval(intervalId);
            toast.success('Instância conectada com sucesso!');
            setOpenQrDialog(false);
            fetchInstancias();
          }
        } catch (error) {
          // Não mostrar erro para o usuário no polling, apenas log
          console.warn('Erro no polling de status (ignorado):', error);
        }
      }, 5000); // Verificar a cada 5 segundos
    }
    
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [openQrDialog, selectedInstancia]);

  const fetchInstancias = async () => {
    setLoading(true);
    try {
      const response = await api.get('/instancias');
      console.log("Dados das instâncias recebidos:", response.data);
      setInstancias(response.data);
    } catch (error) {
      handleError(error, 'Erro ao carregar instâncias');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateInstancia = async () => {
    try {
      if (!formData.nome || !formData.numero_whatsapp) {
        toast.error('Nome e número do WhatsApp são obrigatórios');
        return;
      }

      const response = await api.post('/instancias', formData);
      toast.success('Instância criada com sucesso!');
      setOpenDialog(false);
      
      setSelectedInstancia(response.data);
      fetchQrCode(response.data.idInstancia);
      setOpenQrDialog(true);
      
      setFormData({ 
        nome: '', 
        numero_whatsapp: '', 
        token: uuidv4() 
      });
      
      fetchInstancias();
    } catch (error) {
      handleError(error, 'Erro ao criar instância');
    }
  };

  const fetchQrCode = async (idInstancia) => {
    try {
      setQrCodeLoading(true);
      const response = await api.get(`/instancias/${idInstancia}/qrcode`, {
        responseType: 'json'
      });
      setQrCodeData(response.data.qrcode);
    } catch (error) {
      handleError(error, 'Erro ao obter QR Code');
    } finally {
      setQrCodeLoading(false);
    }
  };

  const handleShowQrCode = (instancia) => {
    setSelectedInstancia(instancia);
    fetchQrCode(instancia.idInstancia);
    setOpenQrDialog(true);
  };

  const handleDeleteInstancia = async (idInstancia) => {
    if (window.confirm('Tem certeza que deseja excluir esta instância?')) {
      try {
        await api.delete(`/instancias/${idInstancia}`);
        toast.success('Instância excluída com sucesso');
        fetchInstancias();
      } catch (error) {
        handleError(error, 'Erro ao excluir instância');
      }
    }
  };

  const handleUpdateConfig = async () => {
    try {
      // Para configurações, usar o token da instância específica
      const instanciaSelecionada = selectedInstancia;
      const tokenDaInstancia = instanciaSelecionada?.token;

      if (!tokenDaInstancia) {
        toast.error('Token da instância não encontrado para atualizar configuração.');
        return;
      }

      await api.put(
        `/instancias/${selectedInstancia.idInstancia}/configuracao`,
        configData,
        {
          headers: {
            'Authorization': `Bearer ${tokenDaInstancia}`
          }
        }
      );

      // Persistir rate-limit (endpoint separado, requer token de instância).
      const ratePayload = {};
      const mpm = parseInt(rateLimitData.mensagens_por_minuto);
      if (Number.isFinite(mpm)) ratePayload.mensagens_por_minuto = mpm;
      if (rateLimitData.idade_conta_meses !== '' && rateLimitData.idade_conta_meses != null) {
        ratePayload.idade_conta_meses = parseInt(rateLimitData.idade_conta_meses);
      }
      if (Object.keys(ratePayload).length > 0) {
        await api.put(
          `/instancias/${selectedInstancia.idInstancia}/rate-limit`,
          ratePayload,
          { headers: { 'Authorization': `Bearer ${tokenDaInstancia}` } }
        );
      }

      toast.success('Configurações atualizadas com sucesso');
      setOpenConfigDialog(false);
      fetchInstancias();
    } catch (error) {
      handleError(error, 'Erro ao atualizar configurações');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'conectado':
        return 'success';
      case 'desconectado':
        return 'error';
      default:
        return 'warning';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">Instâncias</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenDialog(true)}
        >
          Nova Instância
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Número</TableCell>
              <TableCell>ID da Instância</TableCell>
              <TableCell>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {instancias.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  Nenhuma instância encontrada
                </TableCell>
              </TableRow>
            ) : (
              instancias.map((instancia) => (
                <TableRow key={instancia.idInstancia}>
                  <TableCell>{instancia.nome}</TableCell>
                  <TableCell>
                    <Chip
                      label={instancia.status_conexao}
                      color={getStatusColor(instancia.status_conexao)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{instancia.numero_whatsapp || '-'}</TableCell>
                  <TableCell>{instancia.idInstancia}</TableCell>
                  <TableCell>
                    <IconButton
                      onClick={() => handleShowQrCode(instancia)}
                      disabled={instancia.status_conexao === 'conectado'}
                      title={instancia.status_conexao === 'conectado' ? 'Já conectado' : 'Mostrar QR Code'}
                    >
                      <QrCodeIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => {
                        setSelectedInstancia(instancia);
                        // Garantir que configData sempre tenha um valor válido
                        const defaultConfig = {
                          rejeitar_ligacao: false,
                          mensagem_ligacao_rejeitada: '',
                          marcar_mensagem_como_lida: false,
                          mostrar_sempre_online: false
                        };
                        
                        // Processar a configuração da instância
                        if (instancia.configuracao) {
                          if (typeof instancia.configuracao === 'object' && !Array.isArray(instancia.configuracao)) {
                            setConfigData(instancia.configuracao);
                          } else if (typeof instancia.configuracao === 'string') {
                            try {
                              const config = JSON.parse(instancia.configuracao);
                              setConfigData(config);
                            } catch (error) {
                              console.error('Erro ao interpretar configuração:', error);
                              setConfigData(defaultConfig);
                            }
                          } else {
                            setConfigData(defaultConfig);
                          }
                        } else {
                          setConfigData(defaultConfig);
                        }

                        // Popular rate limit a partir dos campos da própria row da instancia.
                        setRateLimitData({
                          mensagens_por_minuto: Number(instancia.mensagens_por_minuto) || 15,
                          idade_conta_meses: instancia.idade_conta_meses ?? ''
                        });

                        setOpenConfigDialog(true);
                      }}
                    >
                      <SettingsIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => handleDeleteInstancia(instancia.idInstancia)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog de Nova Instância */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Nova Instância</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nome da Instância"
            fullWidth
            value={formData.nome}
            onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
          />
          <TextField
            margin="dense"
            label="Número do WhatsApp"
            fullWidth
            placeholder="Ex: 5511999999999"
            helperText="Inclua o código do país e DDD sem símbolos"
            value={formData.numero_whatsapp}
            onChange={(e) => setFormData({ ...formData, numero_whatsapp: e.target.value })}
          />
          <TextField
            margin="dense"
            label="Token (opcional)"
            fullWidth
            value={formData.token}
            onChange={(e) => setFormData({ ...formData, token: e.target.value })}
            helperText="Um token único será gerado automaticamente se não for fornecido"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button onClick={handleCreateInstancia} variant="contained">
            Criar e Conectar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de QR Code */}
      <Dialog 
        open={openQrDialog} 
        onClose={() => setOpenQrDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {selectedInstancia?.status_conexao === 'conectado' 
            ? 'Conectado!' 
            : 'Escaneie o QR Code para conectar'
          }
        </DialogTitle>
        <DialogContent>
          {qrCodeLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress />
            </Box>
          ) : qrCodeData ? (
            <Box sx={{ textAlign: 'center', my: 2 }}>
              <img
                src={`data:image/png;base64,${qrCodeData}`}
                alt="QR Code"
                style={{ maxWidth: '100%' }}
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Abra o WhatsApp no seu celular, acesse Configurações &gt; Aparelhos conectados &gt; Conectar um aparelho
              </Typography>
            </Box>
          ) : (
            <Typography color="error">
              Não foi possível obter o QR Code. Tente novamente.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenQrDialog(false)}>Fechar</Button>
          <Button 
            onClick={() => fetchQrCode(selectedInstancia.idInstancia)} 
            disabled={qrCodeLoading}
            variant="outlined"
          >
            Atualizar QR Code
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de Configurações */}
      <Dialog open={openConfigDialog} onClose={() => setOpenConfigDialog(false)}>
        <DialogTitle>Configurações da Instância</DialogTitle>
        <DialogContent>
          {selectedInstancia && configData && (
            <Box sx={{ mt: 2 }}>
              <TextField
                label="ID da Instância"
                value={selectedInstancia.idInstancia}
                fullWidth
                disabled
                margin="dense"
              />
              <TextField
                label="Token de Autenticação da Instância"
                value={selectedInstancia.token}
                fullWidth
                disabled
                margin="dense"
                helperText="Este token é usado para autenticar chamadas de API para esta instância específica."
              />
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={configData.marcar_mensagem_como_lida || false}
                      onChange={(e) =>
                        setConfigData({
                          ...configData,
                          marcar_mensagem_como_lida: e.target.checked
                        })
                      }
                    />
                  }
                  label="Marcar mensagens como lida"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={configData.rejeitar_ligacao || false}
                      onChange={(e) =>
                        setConfigData({
                          ...configData,
                          rejeitar_ligacao: e.target.checked
                        })
                      }
                    />
                  }
                  label="Recusar Ligações"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={configData.mostrar_sempre_online || false}
                      onChange={(e) =>
                        setConfigData({
                          ...configData,
                          mostrar_sempre_online: e.target.checked
                        })
                      }
                    />
                  }
                  label="Mostrar sempre online"
                />
              </FormGroup>
              
              <TextField
                margin="dense"
                label="Mensagem de Rejeição de Ligação"
                fullWidth
                multiline
                rows={3}
                value={configData.mensagem_ligacao_rejeitada || ''}
                onChange={(e) =>
                  setConfigData({
                    ...configData,
                    mensagem_ligacao_rejeitada: e.target.value
                  })
                }
                disabled={configData && configData.rejeitar_ligacao === false}
              />

              {/* === Seção de Rate Limit (anti-bloqueio) === */}
              <Divider sx={{ my: 3 }} />
              <Typography variant="h6" gutterBottom>
                Limite de envio (anti-bloqueio)
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                Quanto menor o limite, menor o risco de bloqueio pelo WhatsApp.
                Ajuste de acordo com a idade e maturidade da conta.
              </Alert>

              <FormControl fullWidth margin="dense" size="small">
                <InputLabel id="perfil-conta-label">Perfil sugerido (preenche o limite)</InputLabel>
                <Select
                  labelId="perfil-conta-label"
                  label="Perfil sugerido (preenche o limite)"
                  value=""
                  displayEmpty
                  onChange={(e) => {
                    const sel = sugestoesRate.find(s => s.perfil === e.target.value);
                    if (sel) {
                      setRateLimitData(prev => ({
                        ...prev,
                        mensagens_por_minuto: sel.mensagens_por_minuto
                      }));
                    }
                  }}
                >
                  <MenuItem value="" disabled>
                    <em>Selecione um perfil para aplicar a sugestão...</em>
                  </MenuItem>
                  {sugestoesRate.map(s => (
                    <MenuItem key={s.perfil} value={s.perfil}>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {s.perfil.charAt(0).toUpperCase() + s.perfil.slice(1)} — {s.mensagens_por_minuto} msg/min
                          {' '}<Typography component="span" variant="caption" color="text.secondary">
                            ({s.idade_conta_meses})
                          </Typography>
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {s.descricao}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box sx={{ mt: 2, px: 1 }}>
                <Tooltip title="Quantas mensagens este número pode enviar por minuto. Mensagens excedentes ficam na fila." arrow>
                  <Typography variant="body2" gutterBottom>
                    Mensagens por minuto: <strong>{rateLimitData.mensagens_por_minuto}</strong>
                  </Typography>
                </Tooltip>
                <Slider
                  value={Number(rateLimitData.mensagens_por_minuto) || 15}
                  onChange={(_, v) =>
                    setRateLimitData(prev => ({ ...prev, mensagens_por_minuto: v }))
                  }
                  min={1}
                  max={100}
                  step={1}
                  marks={[
                    { value: 5, label: '5' },
                    { value: 15, label: '15' },
                    { value: 25, label: '25' },
                    { value: 50, label: '50' },
                    { value: 100, label: '100' }
                  ]}
                  valueLabelDisplay="auto"
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                <TextField
                  margin="dense"
                  label="Mensagens/minuto (manual)"
                  type="number"
                  size="small"
                  inputProps={{ min: 1, max: 300 }}
                  value={rateLimitData.mensagens_por_minuto}
                  onChange={(e) =>
                    setRateLimitData(prev => ({
                      ...prev,
                      mensagens_por_minuto: e.target.value === '' ? '' : Math.max(1, Math.min(300, parseInt(e.target.value) || 1))
                    }))
                  }
                  helperText="1 a 300"
                  sx={{ flex: 1 }}
                />
                <TextField
                  margin="dense"
                  label="Idade da conta (meses)"
                  type="number"
                  size="small"
                  inputProps={{ min: 0, max: 240 }}
                  value={rateLimitData.idade_conta_meses}
                  onChange={(e) =>
                    setRateLimitData(prev => ({
                      ...prev,
                      idade_conta_meses: e.target.value
                    }))
                  }
                  helperText="Opcional, para auditoria"
                  sx={{ flex: 1 }}
                />
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenConfigDialog(false)}>Cancelar</Button>
          <Button 
            onClick={handleUpdateConfig} 
            variant="contained"
            disabled={!configData}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Instancias; 